/* Just Silver — product page enhancements.

   PART ONE — buy-column sticky behaviour.

   The theme pins the buy column to the TOP of the viewport. That is correct
   only while the column is shorter than the viewport. On a product with three
   accordions the column runs to ~1200px against an ~800px viewport, so once it
   pinned, its last few hundred pixels — the delivery line and every accordion —
   sat below the fold and stayed unreachable until the entire image stack had
   scrolled past. On a fourteen-image product that is twelve thousand pixels.

   Fix: when the column is taller than the viewport, pin its BOTTOM instead of
   its top by setting a negative --pdp-content-top. The column then scrolls with
   the page until its bottom edge meets the viewport bottom, and holds there —
   so the accordions are always reachable. Title, price and the CTA scroll away,
   but the sticky add-to-cart bar already carries all three.

   Re-measures on resize and whenever the column's own height changes, which is
   what happens every time someone opens or closes an accordion.
*/
(() => {
  const SELECTOR = '.pdp--media-scroll .pdp__content-wrapper';
  const BREAKPOINT = window.matchMedia('(min-width: 48em)');
  const GUTTER = 24;

  const headerHeight = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--sticky-header-height');
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const measure = (wrapper) => {
    if (!BREAKPOINT.matches) {
      wrapper.style.removeProperty('--pdp-content-top');
      return;
    }
    const content = wrapper.firstElementChild;
    if (!content) return;
    const top = headerHeight();
    const needed = content.offsetHeight + top + GUTTER;
    // Taller than the viewport -> pin the bottom. Otherwise leave the theme's
    // top alignment alone.
    wrapper.style.setProperty(
      '--pdp-content-top',
      needed > window.innerHeight
        ? `${window.innerHeight - content.offsetHeight - GUTTER}px`
        : `${top}px`
    );
  };

  const init = () => {
    const wrapper = document.querySelector(SELECTOR);
    if (!wrapper || !wrapper.firstElementChild) return;

    const run = () => measure(wrapper);
    run();

    if ('ResizeObserver' in window) {
      new ResizeObserver(run).observe(wrapper.firstElementChild);
    }
    window.addEventListener('resize', run, { passive: true });
    BREAKPOINT.addEventListener('change', run);
    // The theme swaps chunks of the buy column on variant change.
    document.addEventListener('variant:change', run);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* PART TWO — make a sold-out button actually disabled.

   The theme marks an unavailable variant's submit button with aria-disabled="true"
   plus pointer-events: none, and leaves the `disabled` property false and
   tabindex="0" in place. pointer-events only blocks a mouse: a keyboard user can
   still Tab to a "Sold out" button and press Enter, and the form posts to
   /cart/add. Mirroring aria-disabled onto the real disabled property closes that,
   and a MutationObserver keeps it in step as the shopper changes variant.
*/
(() => {
  const SELECTOR = 'button[name="add"]';

  const sync = (button) => {
    const off = button.getAttribute('aria-disabled') === 'true';
    if (button.disabled !== off) button.disabled = off;
    if (off) {
      button.setAttribute('tabindex', '-1');
    } else {
      button.removeAttribute('tabindex');
    }
  };

  const syncAll = () => document.querySelectorAll(SELECTOR).forEach(sync);

  const init = () => {
    syncAll();
    // The theme replaces these buttons wholesale on variant change, so watch the
    // subtree rather than the individual nodes.
    new MutationObserver(syncAll).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-disabled'],
    });
    document.addEventListener('variant:change', syncAll);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
